import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { all_routes } from "../../Router/all_routes";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  FileText,
  Calculator,
  Settings2,
  Edit,
  PlusCircle,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { isStoreChargingTax } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { ReactSelect } from "@/components/ui/react-select";
import CustomerModal from "../../components/modals/CustomerModal";
import ProductModal from "../../components/modals/ProductModal";
import { Country, State } from "country-state-city";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const emptyItem = () => ({
  product_id: null,
  product: null,
  is_new_item: false,
  item_name: "",
  item_description: "",
  item_sku: "",
  quantity: 1,
  unit_price: 0,
  original_price: 0,
  discount_amount: 0,
  discount_type: "fixed",
  discount_value: 0,
  tax_amount: 0,
  tax_percentage: 0,
  line_total: 0,
  notes: "",
});

const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

/**
 * Calculate item-level discount, tax and line total.
 * Mirrors newSale calculateTaxValues:
 *   - isStoreChargingTax(store) && product.is_taxable must both be true
 *   - exclusive: tax added on top, line_total stays pre-tax, tax_amount shown separately
 *   - inclusive: price already contains tax - taxAmount=0, line_total=discountedPrice
 *   - custom items (no product): no automatic tax
 */
const calculateLineTotal = (item, storeConfig = {}) => {
  const qty = parseFloat(item.quantity || 0);
  const unitPrice = parseFloat(item.unit_price || 0);
  const basePrice = round2(qty * unitPrice);

  // Item-level discount
  let discountAmt = 0;
  const dVal = parseFloat(item.discount_value || 0);
  if (item.discount_type === "percentage" && dVal > 0) {
    discountAmt = round2((basePrice * dVal) / 100);
  } else if (item.discount_type === "fixed" && dVal > 0) {
    discountAmt = round2(dVal);
  }

  const discountedPrice = round2(basePrice - discountAmt);

  // Tax
  const product = item.product;
  const chargesTax = isStoreChargingTax(storeConfig) && product?.is_taxable;

  let taxAmt = 0;
  const lineTotal = discountedPrice; // line_total is always pre-tax (exclusive adds tax separately)

  if (chargesTax) {
    const taxRate = parseFloat(storeConfig.tax_percentage || 0);
    const taxMethod = product?.tax_method || "exclusive";
    if (taxMethod === "exclusive") {
      // Add tax on top, display separately - lineTotal stays pre-tax
      taxAmt = round2((discountedPrice * taxRate) / 100);
    }
    // inclusive: price already includes tax - taxAmt stays 0, lineTotal unchanged
  }

  return {
    discount_amount: round2(discountAmt),
    tax_amount: round2(taxAmt),
    line_total: round2(lineTotal),
  };
};

const NewQuotation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);
  const store_id = currentStore?.id || null;
  const userId = useSelector((state) => state.user_data)?.id || null;

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";


  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [quotation, setQuotation] = useState({
    customer_id: null,
    store_id: store_id || "",
    user_id: userId || "",
    quotation_date: new Date().toISOString().split("T")[0],
    valid_until: "",
    status: "draft",
    notes: "",
    discount_type: "fixed",
    discount_value: 0,
  });

  const [items, setItems] = useState([emptyItem()]);
  const [productSearch, setProductSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [configModalIndex, setConfigModalIndex] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [activeRowIndex, setActiveRowIndex] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(productSearch), 400);
    return () => clearTimeout(timer);
  }, [productSearch]);

  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [customerLabels, setCustomerLabels] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setCustomerSearchTerm(customerSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  const { data: customers = [], isFetching: isCustomersFetching } = useQuery({
    queryKey: ["customers-select", customerSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.SALES.CUSTOMERS, {
        params: {
          q: customerSearchTerm || undefined,
          per_page: 15,
        },
      });
      return res.data.data || [];
    },
  });

  useEffect(() => {
    if (customers.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCustomerLabels((prev) => {
        const next = { ...prev };
        customers.forEach((c) => {
          let displayName = c.full_name || c.name;
          if (c.customer_type !== "individual" && c.company) {
            displayName = c.company;
          }
          const labels = [displayName, c.phone].filter(Boolean);
          next[String(c.id)] = labels.join(" - ");
        });
        return next;
      });
    }
  }, [customers]);

  const { data: products = [], isFetching: productsLoading } = useQuery({
    queryKey: ["products-select", debouncedSearch],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.PRODUCTS, {
        params: { per_page: 50, page: 1, search: debouncedSearch },
      });
      return res.data.data || [];
    },
    staleTime: 60 * 1000 * 5,
    placeholderData: (prev) => prev,
  });

  // Auto-calculate totals
  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity || 0);
      const price = parseFloat(item.unit_price || 0);
      return sum + qty * price;
    }, 0);

    const itemsDiscountTotal = items.reduce(
      (sum, item) => sum + parseFloat(item.discount_amount || 0),
      0,
    );
    const itemsTaxTotal = items.reduce(
      (sum, item) => sum + parseFloat(item.tax_amount || 0),
      0,
    );
    const itemsLineTotal = items.reduce(
      (sum, item) => sum + parseFloat(item.line_total || 0),
      0,
    );

    // Header-level discount
    const dVal = parseFloat(quotation.discount_value || 0);
    let headerDiscount = 0;
    if (quotation.discount_type === "percentage") {
      headerDiscount = itemsLineTotal * (dVal / 100);
    } else {
      headerDiscount = dVal;
    }

    const totalDiscount = headerDiscount;
    // Grand total = pre-tax line totals + tax - header discount (mirrors newSale)
    const total = Math.max(
      0,
      round2(itemsLineTotal + itemsTaxTotal - headerDiscount),
    );

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      tax_amount: parseFloat(itemsTaxTotal.toFixed(2)),
      item_discount_amount: parseFloat(itemsDiscountTotal.toFixed(2)),
      discount_amount: parseFloat(totalDiscount.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
      item_count: items.length,
    };
  }, [items, quotation.discount_type, quotation.discount_value]);

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // When product is selected, auto-fill fields
      if (field === "product_id" && value) {
        const product = products.find((p) => p.id === value);
        if (product) {
          updated[index].item_name = product.name || "";
          updated[index].item_sku = product.sku || "";
          updated[index].unit_price = parseFloat(
            product.sell_price || product.price || 0,
          );
          updated[index].original_price = parseFloat(
            product.sell_price || product.price || 0,
          );
          updated[index].product = product;
        }
      }
      if (field === "product_id" && !value) {
        updated[index].product = null;
      }

      // Recalculate line total
      const computed = calculateLineTotal(updated[index], currentStore);
      updated[index] = { ...updated[index], ...computed };

      return updated;
    });
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);

  const removeItem = (index) => {
    if (items.length === 1) {
      toast.warning("At least one item is required");
      return;
    }
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.post(API_ENDPOINTS.QUOTATIONS.QUOTATIONS, data);
      return res.data;
    },
    onSuccess: async () => {
      toast.success("Quotation created successfully");
      await queryClient.invalidateQueries({
        queryKey: ["quotations"],
        refetchType: "all",
      });
      navigate(all_routes.quotationsList);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create quotation",
      );
    },
  });

  const handleSubmit = (e, status) => {
    e.preventDefault();

    if (!quotation.store_id) {
      toast.error("Store is required");
      return;
    }

    const hasInvalidItems = items.some(
      (item) =>
        !item.item_name &&
        !item.product_id &&
        parseFloat(item.quantity || 0) <= 0,
    );
    if (hasInvalidItems) {
      toast.error(
        "Please fill in item name/product and quantity for all items",
      );
      return;
    }

    const payload = {
      ...quotation,
      status: status || quotation.status,
      quotation_date: quotation.quotation_date
        ? new Date(quotation.quotation_date).toISOString()
        : null,
      valid_until: quotation.valid_until
        ? new Date(quotation.valid_until).toISOString()
        : null,
      subtotal: totals.subtotal,
      tax_amount: totals.tax_amount,
      discount_amount: totals.discount_amount,
      total: totals.total,
      item_count: totals.item_count,
      discount_value: parseFloat(quotation.discount_value || 0),
      items: items.map((item) => ({
        product_id: item.product_id || null,
        is_new_item: item.is_new_item || false,
        item_name: item.item_name || item.product?.name || "",
        item_description: item.item_description || "",
        item_sku: item.item_sku || "",
        quantity: parseFloat(item.quantity || 1),
        unit_price: parseFloat(item.unit_price || 0),
        original_price: parseFloat(item.original_price || item.unit_price || 0),
        discount_amount: parseFloat(item.discount_amount || 0),
        discount_type: item.discount_type || "fixed",
        discount_value: parseFloat(item.discount_value || 0),
        tax_amount: parseFloat(item.tax_amount || 0),
        tax_percentage: parseFloat(item.tax_percentage || 0),
        line_total: parseFloat(item.line_total || 0),
        notes: item.notes || "",
      })),
    };

    createMutation.mutate(payload);
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="New Quotation"
          subtitle="Create a new quotation for a customer"
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            onClick={() => navigate(all_routes.quotationsList)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button
            onClick={(e) => handleSubmit(e, quotation.status)}
            disabled={createMutation.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            {createMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <form onSubmit={(e) => handleSubmit(e, quotation.status)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main form - left 2 cols */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header details */}
            <Card>
              <CardHeader>
                <CardTitle>Quotation Details</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Customer */}
                <div className="space-y-1.5">
                  <Label>Customer</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <ReactSelect
                        isClearable
                        onInputChange={(value) => setCustomerSearchInput(value)}
                        filterOption={() => true}
                        placeholder="Select customer..."
                        value={
                          quotation.customer_id
                            ? {
                              value: quotation.customer_id,
                              label:
                                customerLabels[
                                String(quotation.customer_id)
                                ] ||
                                customers.find(
                                  (c) => c.id === quotation.customer_id,
                                )?.name ||
                                customers.find(
                                  (c) => c.id === quotation.customer_id,
                                )?.full_name ||
                                "Customer",
                            }
                            : null
                        }
                        onChange={(opt) =>
                          setQuotation((q) => ({
                            ...q,
                            customer_id: opt ? opt.value : null,
                          }))
                        }
                        options={customers.map((c) => {
                          let displayName = c.full_name || c.name;
                          if (c.customer_type !== "individual" && c.company) {
                            displayName = c.company;
                          }
                          const labels = [displayName, c.phone].filter(Boolean);
                          return {
                            value: c.id,
                            label: labels.join(" - "),
                          };
                        })}
                        isLoading={isCustomersFetching}
                      />
                    </div>
                    {hasPermission("customer create") && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={() => setShowCustomerModal(true)}
                        title="Add New Customer"
                      >
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select
                    value={quotation.status}
                    onValueChange={(val) =>
                      setQuotation((q) => ({ ...q, status: val }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Quotation Date */}
                <div className="space-y-1.5">
                  <Label>Quotation Date</Label>
                  <Input
                    type="date"
                    value={quotation.quotation_date}
                    onChange={(e) =>
                      setQuotation((q) => ({
                        ...q,
                        quotation_date: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Valid Until */}
                <div className="space-y-1.5">
                  <Label>Valid Until</Label>
                  <Input
                    type="date"
                    value={quotation.valid_until}
                    onChange={(e) =>
                      setQuotation((q) => ({
                        ...q,
                        valid_until: e.target.value,
                      }))
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* Items */}
            {hasPermission("quotation item read") && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Line Items</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="px-5">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8">#</TableHead>
                          <TableHead className="min-w-[220px]">Item</TableHead>
                          <TableHead className="min-w-[80px]">Qty</TableHead>
                          <TableHead className="min-w-[110px]">
                            Unit Price
                          </TableHead>
                          <TableHead className="min-w-[110px] text-right">
                            Line Total
                          </TableHead>
                          <TableHead className="w-10 text-center"></TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="text-muted-foreground text-sm align-top pt-3">
                              {index + 1}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5 min-w-[200px]">
                                <div className="flex-1">
                                  <ReactSelect
                                    isLoading={productsLoading}
                                    onInputChange={(val) =>
                                      setProductSearch(val)
                                    }
                                    filterOption={() => true}
                                    isClearable
                                    placeholder="Select product..."
                                    value={
                                      item.product_id
                                        ? {
                                          value: item.product_id,
                                          label:
                                            item.product?.name ||
                                            products.find(
                                              (p) => p.id === item.product_id,
                                            )?.name ||
                                            "Product",
                                        }
                                        : null
                                    }
                                    onChange={(opt) =>
                                      updateItem(
                                        index,
                                        "product_id",
                                        opt ? opt.value : null,
                                      )
                                    }
                                    options={products.map((p) => ({
                                      value: p.id,
                                      label: p.name,
                                    }))}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="shrink-0"
                                  onClick={() => {
                                    setActiveRowIndex(index);
                                    setShowProductModal(true);
                                  }}
                                  title="Add New Product"
                                >
                                  <PlusCircle className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="align-top pt-3">
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                value={item.quantity}
                                onChange={(e) =>
                                  updateItem(index, "quantity", e.target.value)
                                }
                                className="h-8 w-20"
                              />
                            </TableCell>
                            <TableCell className="align-top pt-3">
                              <div className="flex items-center gap-1">
                                <span className="text-muted-foreground text-xs">
                                  {currencySymbol}
                                </span>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.unit_price}
                                  onChange={(e) =>
                                    updateItem(
                                      index,
                                      "unit_price",
                                      e.target.value,
                                    )
                                  }
                                  className="h-8 w-24"
                                />
                              </div>
                            </TableCell>
                            <TableCell className="text-right align-top pt-3">
                              <div className="font-medium">
                                {currencySymbol}
                                {parseFloat(item.line_total || 0).toFixed(2)}
                              </div>
                              {(parseFloat(item.discount_value || 0) > 0 ||
                                parseFloat(item.tax_amount || 0) > 0) && (
                                  <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                                    {parseFloat(item.discount_value || 0) > 0 && (
                                      <div>
                                        Disc:{" "}
                                        {item.discount_type === "percentage"
                                          ? `${parseFloat(item.discount_value).toFixed(2)}%`
                                          : `${currencySymbol}${parseFloat(item.discount_amount || 0).toFixed(2)}`}
                                      </div>
                                    )}
                                    {parseFloat(item.tax_amount || 0) > 0 && (
                                      <div>
                                        Tax (
                                        {item.product?.tax_method === "inclusive"
                                          ? "incl"
                                          : `${parseFloat(currentStore?.tax_percentage || 0).toFixed(0)}%`}
                                        ): {currencySymbol}
                                        {parseFloat(item.tax_amount).toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                )}
                            </TableCell>
                            <TableCell className="align-top pt-2">
                              {hasPermission("quotation item update") && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                  title="Configure item (discount, tax, notes)"
                                  onClick={() => setConfigModalIndex(index)}
                                >
                                  <Settings2 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                            <TableCell className="align-top pt-2">
                              {hasPermission("quotation item delete") && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-foreground"
                                  onClick={() => removeItem(index)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
                <CardFooter className="border-t pt-3">
                  {hasPermission("quotation item create") && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={addItem}
                      className="text-muted-foreground"
                    >
                      <Plus className="mr-1.5 h-4 w-4" />
                      Add another line
                    </Button>
                  )}
                </CardFooter>
              </Card>
            )}

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Additional notes for this quotation..."
                  value={quotation.notes}
                  onChange={(e) =>
                    setQuotation((q) => ({ ...q, notes: e.target.value }))
                  }
                  rows={4}
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - totals & discount */}
          <div className="space-y-6">
            {/* Summary */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-muted-foreground" />
                  <CardTitle>Summary</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>
                    {currencySymbol}
                    {totals.subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Item Discounts</span>
                  <span className="text-destructive">
                    -{currencySymbol}
                    {(totals.item_discount_amount || 0).toFixed(2)}
                  </span>
                </div>
                {totals.discount_amount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Quotation Discount
                    </span>
                    <span className="text-destructive">
                      -{currencySymbol}
                      {(totals.discount_amount || 0).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>
                    {currencySymbol}
                    {totals.tax_amount.toFixed(2)}
                  </span>
                </div>

                {/* Header discount */}
                <Separator />
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Additional Discount
                  </Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={quotation.discount_type}
                      onValueChange={(val) =>
                        setQuotation((q) => ({ ...q, discount_type: val }))
                      }
                    >
                      <SelectTrigger className="h-8 w-28 shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">
                          {currencySymbol} Fixed
                        </SelectItem>
                        <SelectItem value="percentage">% Percent</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="h-8"
                      value={quotation.discount_value}
                      onChange={(e) =>
                        setQuotation((q) => ({
                          ...q,
                          discount_value: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <Separator />
                <div className="flex items-center justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-lg">
                    {currencySymbol}
                    {totals.total.toFixed(2)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground text-right">
                  {totals.item_count} item{totals.item_count !== 1 ? "s" : ""}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>

      {/* Per-item configuration modal */}
      {configModalIndex !== null && items[configModalIndex] && (
        <Dialog
          open={configModalIndex !== null}
          onOpenChange={() => setConfigModalIndex(null)}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Configure Item</DialogTitle>
              <DialogDescription>
                {items[configModalIndex]?.item_name ||
                  `Item ${configModalIndex + 1}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* SKU */}
              <div className="space-y-1.5">
                <Label>SKU</Label>
                <Input
                  placeholder="Item SKU..."
                  value={items[configModalIndex]?.item_sku || ""}
                  onChange={(e) =>
                    updateItem(configModalIndex, "item_sku", e.target.value)
                  }
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea
                  placeholder="Item description..."
                  value={items[configModalIndex]?.item_description || ""}
                  onChange={(e) =>
                    updateItem(
                      configModalIndex,
                      "item_description",
                      e.target.value,
                    )
                  }
                  rows={2}
                />
              </div>

              {/* Discount */}
              <div className="space-y-1.5">
                <Label>Discount</Label>
                <div className="flex gap-2">
                  <Select
                    value={items[configModalIndex]?.discount_type}
                    onValueChange={(val) =>
                      updateItem(configModalIndex, "discount_type", val)
                    }
                  >
                    <SelectTrigger className="w-32 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">
                        {currencySymbol} Fixed
                      </SelectItem>
                      <SelectItem value="percentage">% Percent</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={items[configModalIndex]?.discount_value || 0}
                    onChange={(e) =>
                      updateItem(
                        configModalIndex,
                        "discount_value",
                        e.target.value,
                      )
                    }
                  />
                </div>
                {parseFloat(items[configModalIndex]?.discount_amount || 0) >
                  0 && (
                    <p className="text-xs text-muted-foreground">
                      Discount amount: {currencySymbol}
                      {parseFloat(
                        items[configModalIndex]?.discount_amount || 0,
                      ).toFixed(2)}
                    </p>
                  )}
              </div>

              {/* Tax - informational, auto-applied from store + product settings */}
              <div className="space-y-1.5">
                <Label>Tax</Label>
                {(() => {
                  const item = items[configModalIndex];
                  const product = item?.product;
                  const storeCharges = isStoreChargingTax(currentStore);
                  const isProductTaxable = product?.is_taxable;
                  const taxMethod = product?.tax_method || "exclusive";
                  const taxRate = parseFloat(currentStore?.tax_percentage || 0);
                  const taxAmt = parseFloat(item?.tax_amount || 0);
                  if (!product) {
                    return (
                      <p className="text-xs text-muted-foreground">
                        No tax - custom item, not linked to a product.
                      </p>
                    );
                  }
                  if (!storeCharges) {
                    return (
                      <p className="text-xs text-muted-foreground">
                        No tax - store does not charge sales tax.
                      </p>
                    );
                  }
                  if (!isProductTaxable) {
                    return (
                      <p className="text-xs text-muted-foreground">
                        No tax - product is not taxable.
                      </p>
                    );
                  }
                  if (taxMethod === "inclusive") {
                    return (
                      <p className="text-xs text-muted-foreground">
                        Tax inclusive ({taxRate}%) - price already includes tax.
                      </p>
                    );
                  }
                  return (
                    <p className="text-xs text-muted-foreground">
                      Tax exclusive - {taxRate}% applied ={" "}
                      <span className="font-medium text-foreground">
                        {currencySymbol}
                        {taxAmt.toFixed(2)}
                      </span>
                    </p>
                  );
                })()}
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <Label>Item Notes</Label>
                <Textarea
                  placeholder="Notes for this item..."
                  value={items[configModalIndex]?.notes || ""}
                  onChange={(e) =>
                    updateItem(configModalIndex, "notes", e.target.value)
                  }
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setConfigModalIndex(null)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <CustomerModal
        open={showCustomerModal}
        onOpenChange={setShowCustomerModal}
        onSuccess={(newCustomer) => {
          let displayName =
            newCustomer.full_name ||
            `${newCustomer.first_name} ${newCustomer.last_name}`;
          if (
            newCustomer.customer_type !== "individual" &&
            newCustomer.company
          ) {
            displayName = newCustomer.company;
          }
          const labels = [displayName, newCustomer.phone].filter(Boolean);
          setCustomerLabels((prev) => ({
            ...prev,
            [String(newCustomer.id)]: labels.join(" - "),
          }));
          setQuotation((q) => ({ ...q, customer_id: newCustomer.id }));
        }}
      />
      <ProductModal
        open={showProductModal}
        onOpenChange={setShowProductModal}
        onSuccess={(newProduct) => {
          if (activeRowIndex !== null) {
            updateItem(activeRowIndex, "product_id", newProduct.id);
          }
        }}
      />
    </PageContainer>
  );
};

export default NewQuotation;
